import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('tnc:performance-profile-change', this.#updatePlayback, {
      signal: this.#abortController.signal,
    });
    this.#updatePlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #updatePlayback = () => {
    const { videoSources, videoElement } = this.refs;

    if (window.tncIsSlowDevice?.()) {
      videoElement.pause();
      videoElement.preload = 'none';
      videoElement.removeAttribute('autoplay');
      videoSources.forEach((source) => source.removeAttribute('src'));
      videoElement.load();
      return;
    }

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // iOS requires muted inline state to be set on the media properties before
    // a programmatic play request, especially when sources are attached lazily.
    videoElement.defaultMuted = true;
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.load();
    videoElement.play().catch(() => {
      // The poster remains visible when the user's device policy blocks autoplay.
    });
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
